import { useCallback, useEffect, useMemo, useState } from "react";

import { DEFAULT_PLAYGROUND_PRODUCT_IMAGE } from "../utils/normalizePlaygroundMediaUrl";

/**
 * Many entity shards use `cdn.faire.com` (works broadly) while others use
 * `cdn.prerelease.faire.dev` (different asset set — same path often 404s on prerelease).
 * Build a small fallback chain so a failed load can try the other host, then a
 * known-good placeholder.
 */
const buildPlaygroundImageFallbackChain = (initial: string): string[] => {
  const chain: string[] = [];
  const pushUnique = (url: string) => {
    if (url.length > 0 && !chain.includes(url)) {
      chain.push(url);
    }
  };

  pushUnique(initial);

  if (/^https?:\/\/cdn\.faire\.com\b/.test(initial)) {
    pushUnique(
      initial.replace(
        /^https?:\/\/cdn\.faire\.com/,
        "https://cdn.prerelease.faire.dev"
      )
    );
  } else if (initial.includes("cdn.prerelease.faire.dev")) {
    pushUnique(
      initial.replace(
        "https://cdn.prerelease.faire.dev",
        "https://cdn.faire.com"
      )
    );
  }

  pushUnique(DEFAULT_PLAYGROUND_PRODUCT_IMAGE);

  return chain;
};

/**
 * Drives `<img src>` with automatic CDN / placeholder fallback on `onError`.
 */
export const usePlaygroundImageWithFallback = (
  primarySrc: string
): { src: string; onError: () => void } => {
  const chain = useMemo(
    () => buildPlaygroundImageFallbackChain(primarySrc),
    [primarySrc]
  );
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [primarySrc]);

  const src =
    chain[Math.min(index, chain.length - 1)] ??
    DEFAULT_PLAYGROUND_PRODUCT_IMAGE;

  const onError = useCallback(() => {
    setIndex((i) => {
      if (i >= chain.length - 1) {
        return i;
      }
      return i + 1;
    });
  }, [chain]);

  return { src, onError };
};
